import React, { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  LocationContextProps,
  useLocationsContext,
} from "../../../context/LocationsContext";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import CountrySelect from "../../sharedComponents/inputs/CountrySelect";
import StateProvinceSelect from "../../sharedComponents/inputs/StateProvinceSelect";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import fieldTrim from "../../supportFunctions/FieldTrim";
import {
  addLocationMutation,
  updateLocationMutation,
} from "../../supportFunctions/graphql/LocationsQueries";
import { getCountryFromCode, getSubdivisionFromName } from "./Country";
import {
  defaultLocation,
  defaultAddress,
  FormLocation,
  FormLocationKeys,
  getLocationFromLocationForm,
  isLocationPhysical,
  Location,
  replaceLocationInLineage,
  getEstablishmentTypes,
  getEstablishmentType,
} from "./Location";
import { Input } from "../../sharedComponents/inputs/Input";
import { Checkbox } from "../../sharedComponents/inputs/Checkbox";
import classNames from "classnames/bind";
import classes from "./AddEditLocationForm.module.scss";
import {
  BaseButton,
  UnsavedChangesPrompt,
  Select,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import { useHistory } from "react-router-dom";
import GatewayService from "../../../services/GatewayService";

export enum Mode {
  Add,
  Edit,
  View,
}

interface AddEditLocationProps {
  mode: Mode;
  location?: Location;
  locationSubmitCallback: (location: Location) => void;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  setShowAddSlider?: Dispatch<SetStateAction<boolean>>;
}

const AddEditLocationForm: React.FC<AddEditLocationProps> = ({
  location = defaultLocation,
  mode = Mode.View,
  locationSubmitCallback = () => {},
  setIsSubmitting = () => {},
  setShowAddSlider = () => {},
}) => {
  const cx = classNames.bind(classes);
  const locationsContext: LocationContextProps = useLocationsContext();
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();
  const history = useHistory();

  const {
    reset,
    control,
    handleSubmit,
    register,
    unregister,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<FormLocation>({
    mode: "onBlur",
    defaultValues: {
      name: location?.name,
      shortCode: location?.shortCode,
      industryDescription: location?.industryDescription,
      address: {
        nation: location?.address.nation,
        address1: location?.address.address1,
        address2: location?.address.address2,
        city: location?.address.city,
        postalCode: location?.address.postalCode,
        subdivision: location?.address.subdivision,
      },
      lineage: location?.lineage,
      locationStatus: location?.locationStatus,
      id: location?.id,
    },
  });

  //Needed to create my own isDirty state since the form is updating this value when rendered
  //making it true.
  const isDirty = Boolean(Object.keys(dirtyFields).length);

  // useWatch will make this value update when the value changes
  const isPhysicalLocation = watch("isPhysicalLocation");
  const countrySelected = watch("address.nation");

  const registerField = (
    isRegistration: boolean,
    fieldName: FormLocationKeys
  ) => {
    if (isRegistration) {
      register(fieldName);
    } else {
      // Retain value, isDirty, defaultValue, and isTouched state
      // without retaining isDirty and defaultValue the form will act like the fields are fresh after re-registering them
      unregister(fieldName, {
        keepValue: true,
        keepDirty: true,
        keepDefaultValue: true,
        keepTouched: true,
      });
    }
  };

  const saveUpdateLocation = (formLocation: FormLocation) => {
    const updatedLocation: Location = getLocationFromLocationForm(
      formLocation,
      location
    );

    const establishmentType =
      typeof formLocation.establishmentType === "string"
        ? getEstablishmentTypes().filter(
            (establishmentType) =>
              establishmentType.id === formLocation.establishmentType
          )[0]
        : formLocation.establishmentType;

    const parsedLocation = { ...updatedLocation, establishmentType };
    return GatewayService.api
      .post("", updateLocationMutation(parsedLocation, isPhysicalLocation))
      .then((response: any) => {
        locationSubmitCallback(updatedLocation);
        showSnackbar?.(SnackbarType.Success, t("location_update_success"));
      })
      .catch((error: any) => {
        /* eslint-disable-next-line no-console */
        console.log(error);
        attemptShowErrorsInSnackbar(showSnackbar, error);
      });
  };

  const addLocation = (formLocation: FormLocation) => {
    const newLocation: Location = getLocationFromLocationForm(formLocation);

    return GatewayService.api
      .post("", addLocationMutation(UsersService.customer.id, newLocation))
      .then((response: any) => {
        newLocation.id = response.data.data.createLocation.id;
        showSnackbar?.(
          SnackbarType.Success,
          t("location_created_successfully")
        );
        setShowAddSlider?.(false);
      })
      .catch((error: any) => {
        /* eslint-disable-next-line no-console */
        console.log(error);
        attemptShowErrorsInSnackbar(showSnackbar, error);
      })
      .finally(() => {
        locationSubmitCallback(newLocation);
      });
  };

  const onSubmit = useCallback(
    (formValues: FormLocation) => {
      const trimmedFormValues = {
        ...formValues,
        name: formValues.name.trim(),
        address: isPhysicalLocation
          ? fieldTrim({ formData: formValues.address })
          : defaultAddress,
      };
      if (mode === Mode.Add) {
        const newLocation = {
          name: trimmedFormValues.name,
          address: trimmedFormValues.address,
          parentLocation: location,
          locationStatus: "Active",
          shortCode: trimmedFormValues?.shortCode,
          industryDescription: trimmedFormValues?.industryDescription,
          establishmentType: trimmedFormValues?.establishmentType,
          sicCode: trimmedFormValues?.sicCode,
          naicsCode: trimmedFormValues?.naicsCode,
          ein: trimmedFormValues?.ein,
        } as Location;

        return addLocation(newLocation);
      } else if (mode === Mode.Edit) {
        const updatedLocation = {
          id: location?.id,
          parentLocation: location?.parentLocation,
          locationStatus: location?.locationStatus,
          name: trimmedFormValues.name,
          shortCode: trimmedFormValues?.shortCode,
          industryDescription: trimmedFormValues?.industryDescription,
          establishmentType: trimmedFormValues?.establishmentType,
          sicCode: trimmedFormValues?.sicCode,
          naicsCode: trimmedFormValues?.naicsCode,
          ein: trimmedFormValues?.ein,
          address: trimmedFormValues.address,
          lineage: replaceLocationInLineage(
            location?.lineage,
            location?.name,
            trimmedFormValues.name
          ),
        } as Location;

        return saveUpdateLocation(updatedLocation);
      }
    },
    //Changing the dependency array would require a different programmatic structure, TODO:Investigate useCallback
    // eslint-disable-next-line
    [location, isPhysicalLocation]
  );

  useEffect(() => {
    // When mode === ADD we only pass in nation and subdivision as default values
    if (mode === Mode.Add) {
      reset({
        name: "",
        shortCode: "",
        industryDescription: "",
        isPhysicalLocation: false,
        address: {
          nation: location?.address?.nation,
          subdivision: location?.address?.subdivision,
        },
      });
    } else if (location) {
      // Must prevent references to prop object
      // If reference to prop object exists inside the reset call, unregistering the field will delete data
      const tempLocation = { ...location, address: { ...location.address } };
      reset({
        ...tempLocation,
        establishmentType: getEstablishmentType(
          location?.establishmentType as string
        ),
        address: tempLocation?.address,
        isPhysicalLocation: isLocationPhysical(location),
      });
    }
    // Initialize selectedCountry to address.nation value if present
    // Only valid when locationsContext is finished loading
    if (Boolean(location) && locationsContext.isCountryListLoaded) {
      const countryValue = getCountryFromCode(
        location?.address?.nation || undefined,
        locationsContext.countryList
      );
      const subValue = getSubdivisionFromName(
        location?.address?.subdivision || undefined,
        countryValue?.subdivisions
      );
      setValue("address.nation", countryValue);
      setValue("address.subdivision", subValue);
    }
    // eslint-disable-next-line
  }, [location, reset, locationsContext.isCountryListLoaded]);

  useEffect(() => {
    // The address fields should only be part of the form is this a physical location
    registerField(isPhysicalLocation, "address.address1");
    registerField(isPhysicalLocation, "address.address2");
    registerField(isPhysicalLocation, "address.city");
    registerField(isPhysicalLocation, "address.nation");
    registerField(isPhysicalLocation, "address.postalCode");
    registerField(isPhysicalLocation, "address.subdivision");
    // eslint-disable-next-line
  }, [isPhysicalLocation]);

  useEffect(() => {
    setIsSubmitting && setIsSubmitting(isSubmitting);
    // eslint-disable-next-line
  }, [isSubmitting]);

  return (
    <>
      <form
        id="addEditLocationForm"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <Input
          control={control}
          name="name"
          label={t("universal_Name")}
          rules={{
            required: t("customer_Form_Name_Required"),
            pattern: /^\s*[^>]+\s*$/i,
            maxLength: {
              value: 50,
              message: t("location_Form_Name_Max_Length_Validation"),
            },
          }}
          disabled={mode === Mode.View}
          errorMessage={errors ? errors.name?.message : undefined}
        />
        <Input
          control={control}
          name="shortCode"
          label={t("location_form_short_code_label")}
          disabled={mode === Mode.View}
          errorMessage={errors ? errors.shortCode?.message : undefined}
        />
        <Input
          control={control}
          name="industryDescription"
          label={t("location_form_industry_description_label")}
          disabled={mode === Mode.View}
          errorMessage={
            errors ? errors.industryDescription?.message : undefined
          }
        />
        <Checkbox
          control={control}
          name="isPhysicalLocation"
          label={t("locations_Physical")}
          disabled={mode === Mode.View}
        />
        {isPhysicalLocation ? (
          <Select
            control={control}
            name="establishmentType"
            label={t("location_form_establishment_type_label")}
            options={getEstablishmentTypes()}
            errorMessage={errors.establishmentType?.message}
          />
        ) : null}
        {isPhysicalLocation && (
          <>
            <CountrySelect
              control={control}
              errors={errors}
              disabled={mode === Mode.View}
            />
            <Input
              control={control}
              label={t("locations_Address_1")}
              name="address.address1"
              rules={{
                required: t("required_message_address"),
                maxLength: {
                  value: 255,
                  message: t("location_Form_Address_Max_Length_Validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={
                errors ? errors.address?.address1?.message : undefined
              }
            />
            <Input
              control={control}
              label={t("locations_Address_2")}
              name="address.address2"
              rules={{
                maxLength: {
                  value: 255,
                  message: t("location_Form_Address_Max_Length_Validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={
                errors ? errors.address?.address2?.message : undefined
              }
            />
            <Input
              control={control}
              label={t("locations_City")}
              name="address.city"
              rules={{
                required: t("required_message_city"),
                maxLength: {
                  value: 100,
                  message: t("max_length_100_char_form_validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.address?.city?.message : undefined}
            />
            <StateProvinceSelect
              country={countrySelected}
              setValue={setValue}
              getValues={getValues}
              control={control}
              disabled={mode === Mode.View || !countrySelected}
              errors={errors}
            />
            <Input
              control={control}
              label={t("locations_Zip_Code")}
              name="address.postalCode"
              rules={{
                maxLength: {
                  value: 16,
                  message: t("location_Form_Postal_Code_Max_Length_Validation"),
                },
              }}
              disabled={mode === Mode.View}
              errorMessage={
                errors ? errors.address?.postalCode?.message : undefined
              }
            />
            <Input
              control={control}
              name="sicCode"
              label={`SIC ${t("universal_lower_code")}`}
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.sicCode?.message : undefined}
            />
            <Input
              control={control}
              name="naicsCode"
              label={`NAICS ${t("universal_lower_code")}`}
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.naicsCode?.message : undefined}
            />
            <Input
              control={control}
              name="ein"
              label="EIN"
              disabled={mode === Mode.View}
              errorMessage={errors ? errors.ein?.message : undefined}
            />
          </>
        )}
        {mode === Mode.Edit && (
          <>
            <BaseButton
              type="button"
              label={t("universal_Cancel")}
              className="p-button-text"
              onClick={() => history.push("/location")}
            />
            <BaseButton
              type="submit"
              form="addEditLocationForm"
              loading={isSubmitting}
              className={cx(classes.saveButton)}
              label={t("universal_Save")}
            />
          </>
        )}
      </form>
      <UnsavedChangesPrompt isDirty={isDirty} history={history} />
    </>
  );
};

export default AddEditLocationForm;
